import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import type { ApexOptions } from "apexcharts";
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  Typography,
} from "@mui/material";
import moment from "moment";
import { formatDateUS } from "@/utils/dateUtils";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";

interface DataItem {
  tokenused?: number;
  month?: string;
  date?: string;
  used: number;
  tenant: number;
}

interface Props {
  data: DataItem[];
  customFrom: string;
  customTo: string;
  filter: string;
  setCustomTo: (val: string) => void;
  setCustomFrom: (val: string) => void;
  setFilter: (val: "yearly" | "monthly" | "custom") => void;
  selectedYear: number;
  setSelectedYear: (val: number) => void;
  selectedMonth: number;
  setSelectedMonth: (val: number) => void;
  isTenant?: boolean;
}

const UserCandleGraph: React.FC<Props> = ({
  data,
  customFrom,
  customTo,
  filter,
  setCustomTo,
  setCustomFrom,
  setFilter,
  selectedYear,
  setSelectedYear,
  selectedMonth,
  isTenant = false,
}) => {
  const [isMobile, setIsMobile] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const MAX_CUSTOM_RANGE_DAYS = 60;

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // ✅ Auto-set default custom dates if empty
  useEffect(() => {
    if (
      filter === "custom" &&
      (!customFrom || customFrom === "") &&
      (!customTo || customTo === "")
    ) {
      const today = dayjs().format("YYYY-MM-DD");
      const yesterday = dayjs().subtract(1, "day").format("YYYY-MM-DD");
      setCustomFrom(yesterday);
      setCustomTo(today);
    }
  }, [filter, customFrom, customTo, setCustomFrom, setCustomTo]);

  // Custom range validation
  useEffect(() => {
    if (filter === "custom" && customFrom && customTo) {
      const from = moment(customFrom);
      const to = moment(customTo);
      const diff = to.diff(from, "days");

      if (diff > MAX_CUSTOM_RANGE_DAYS) {
        setErrorMessage(
          `Date range too long. Please select up to ${MAX_CUSTOM_RANGE_DAYS} days.`,
        );
      } else if (diff < 0) {
        setErrorMessage("Invalid date range. 'From' must be before 'To'.");
      } else {
        setErrorMessage("");
      }
    } else {
      setErrorMessage("");
    }
  }, [customFrom, customTo, filter]);

  let categories: string[] = [];
  let usedValues: number[] = [];
  const MAX_BARS = 100;
  const tooManyBars = data.length > MAX_BARS;

  if (filter === "yearly") {
    categories = data.map((item) => item.month || "");
  } else if (filter === "monthly" || filter === "custom") {
    categories = data.map((item) =>
      item.date ? formatDateUS(item.date).split("/").slice(1, 2).join("") : "",
    );
  }

  usedValues = data.map((item) =>
    isTenant ? (item.tokenused ?? 0) : (item.used ?? 0),
  );

  const chartData = {
    options: {
      chart: {
        id: "usage-bar",
        toolbar: { show: false },
        zoom: { enabled: true },
        foreColor: "#fff",
      },
      title: {
        text: isTenant ? "Token Usage Report" : "Licence Usage Report",
        align: "center",
        style: { fontSize: "20px", fontWeight: "bold", color: "#fff" },
      },
      subtitle: {
        text:
          filter === "yearly"
            ? `Data for ${selectedYear}`
            : filter === "monthly"
              ? `Monthly Report - ${moment()
                  .month(selectedMonth - 1)
                  .format("MMMM")} ${selectedYear}`
              : `Custom Date Range`,
        align: "center",
        style: { fontSize: "14px", color: "#ccc" },
      },
      xaxis: {
        categories,
        title: {
          text: filter === "yearly" ? "Months" : "Date",
          style: { color: "#fff" },
        },
        labels: {
          style: { colors: "#fff" },
          formatter: (val: string) => (isMobile ? val.slice(0, 3) : val),
        },
        axisBorder: { color: "#777" },
        axisTicks: { color: "#777" },
      },
      yaxis: {
        title: {
          text: isTenant ? "Tokens Used" : "Licence Usage",
          style: { color: "#fff" },
        },
        labels: { style: { colors: "#fff" } },
      },
      legend: {
        labels: { colors: "#fff" },
        position: "top",
        horizontalAlign: "right",
        markers: { width: 10, height: 10 },
      },
      tooltip: { theme: "dark" },
      colors: ["#4F46E5"],
      grid: { borderColor: "#444" },
    } as ApexOptions,
    series: [
      {
        name: isTenant ? "Tokens Used" : "Licence Usage",
        data: usedValues,
      },
    ],
  };

  return (
    <div
      style={{
        backgroundColor: "#1e1e2f",
        borderRadius: "12px",
        boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
        color: "#fff",
        padding: "20px",
        margin: "0 auto",
      }}
    >
      <Box
        display="flex"
        flexWrap="wrap"
        justifyContent="space-between"
        mb={2}
        gap={2}
      >
        <FormControl sx={{ minWidth: 150 }} size="small">
          <InputLabel sx={{ color: "#fff" }}>Filter</InputLabel>
          <Select
            value={filter}
            onChange={(e) =>
              setFilter(e.target.value as "yearly" | "monthly" | "custom")
            }
            label="Filter"
            sx={{
              color: "#fff",
              ".MuiOutlinedInput-notchedOutline": { borderColor: "#fff" },
              "& .MuiSvgIcon-root": { color: "#fff" },
            }}
          >
            <MenuItem value="yearly">Yearly</MenuItem>
            <MenuItem value="monthly">Monthly</MenuItem>
            <MenuItem value="custom">Custom</MenuItem>
          </Select>
        </FormControl>

        {filter === "yearly" && (
          <FormControl sx={{ minWidth: 120 }} size="small">
            <InputLabel sx={{ color: "#fff" }}>Year</InputLabel>
            <Select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              label="Year"
              sx={{
                color: "#fff",
                ".MuiOutlinedInput-notchedOutline": { borderColor: "#fff" },
                "& .MuiSvgIcon-root": { color: "#fff" },
              }}
            >
              {Array.from({ length: 5 }, (_, i) => moment().year() - i).map(
                (year) => (
                  <MenuItem key={year} value={year}>
                    {year}
                  </MenuItem>
                ),
              )}
            </Select>
          </FormControl>
        )}

        {filter === "custom" && (
          <>
            <DatePicker
              label="From"
              value={
                customFrom && dayjs(customFrom).isValid()
                  ? dayjs(customFrom)
                  : null
              }
              onChange={(newValue) =>
                setCustomFrom(newValue ? newValue.format("YYYY-MM-DD") : "")
              }
              format="MM/DD/YY"
              maxDate={dayjs()}
              slotProps={{
                textField: {
                  placeholder: "MM/DD/YY",
                  size: "small",
                  inputProps: {
                    placeholder: "MM/DD/YY",
                  },
                  InputProps: {
                    sx: {
                      color: "#fff",
                      "& input": {
                        color: "#fff !important",
                        "&::placeholder": {
                          color: "#fff !important",
                          opacity: 1,
                        },
                      },
                      "& fieldset": {
                        borderColor: "#fff !important",
                      },
                      "& .MuiSvgIcon-root": {
                        color: "#fff !important",
                      },
                    },
                  },
                  InputLabelProps: {
                    sx: {
                      color: "#fff !important",
                    },
                  },
                },
              }}
            />

            <DatePicker
              label="To"
              value={
                customTo && dayjs(customTo).isValid() ? dayjs(customTo) : null
              }
              onChange={(newValue) =>
                setCustomTo(newValue ? newValue.format("YYYY-MM-DD") : "")
              }
              format="MM/DD/YY"
              maxDate={dayjs()}
              slotProps={{
                textField: {
                  placeholder: "MM/DD/YY",
                  size: "small",
                  inputProps: {
                    placeholder: "MM/DD/YY",
                  },
                  InputProps: {
                    sx: {
                      color: "#fff",
                      "& input": {
                        color: "#fff !important",
                        "&::placeholder": {
                          color: "#fff !important",
                          opacity: 1,
                        },
                      },
                      "& fieldset": {
                        borderColor: "#fff !important",
                      },
                      "& .MuiSvgIcon-root": {
                        color: "#fff !important",
                      },
                    },
                  },
                  InputLabelProps: {
                    sx: {
                      color: "#fff !important",
                    },
                  },
                },
              }}
            />
          </>
        )}
      </Box>

      {errorMessage && (
        <Typography variant="body2" color="error" sx={{ mt: 2 }}>
          {errorMessage}
        </Typography>
      )}

      {!errorMessage && tooManyBars && (
        <Typography variant="body2" color="warning.main" sx={{ mt: 2 }}>
          Too much data to display. Please narrow your date range.
        </Typography>
      )}

      {!errorMessage && !tooManyBars && (
        <Chart
          options={chartData.options}
          series={chartData.series}
          type="bar"
          height="350"
        />
      )}
    </div>
  );
};

export default UserCandleGraph;
